import React, { useState, useEffect, useMemo } from "react";
import "./AddNewTrip.css";
import "leaflet/dist/leaflet.css";
import MapView from "../../components/MapView/MapView";
import ApiManager from "../../ApiManager/ApiManager";
import ManualAddressForm from "../../components/ManualAddressForm/ManualAddressForm";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getContrastColor } from "../../utils/GetContrastColor";
import CustomSelect from "../../components/CustomSelect";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import MapInput from "../../components/MapInput";

const AddNewTrip = () => {
  const navigate = useNavigate();
  const { isLoading, loadingStates, loadingMessages, loadingProgress, withLoading, getProgress, getMessage } = useComponentLoading({
    fetchCompanies: false,
    fetchDrivers: false,
    fetchCars: false,
    fetchCarTypes: false,
    fetchTripTypes: false,
    fetchLabels: false,
    fetchGarages: false,
    fetchCities: false,
    fetchBillingItems: false
  });

  const [passengers, setPassengers] = useState([
    { name: "", phone: "", email: "" },
  ]);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [tripTypes, setTripTypes] = useState([]);
  const [tripType, setTripType] = useState(null);
  const [charges, setCharges] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCarTypes, setLoadingCarTypes] = useState(false);
  const [loadingTripTypes, setLoadingTripTypes] = useState(false);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [labels, setLabels] = useState([]);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [selectedBillingItems, setSelectedBillingItems] = useState([]);
  const [billingItemPayload, setBillingItemPayload] = useState([]);
  const [cities, setCities] = useState([]);
  const [showPrices, setShowPrices] = useState(false);
  const [garageData, setGarageData] = useState([]);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFromCity, setSelectedFromCity] = useState(null);
  const [selectedToCity, setSelectedToCity] = useState(null);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [selectedTripType, setSelectedTripType] = useState(null);

  // City location state for autocomplete
  const [fromCityLocation, setFromCityLocation] = useState(null);
  const [toCityLocation, setToCityLocation] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [contractAutoSwitchEnabled, setContractAutoSwitchEnabled] = useState(false);

  // Memoized options arrays
  const companyOptions = useMemo(() => companies.map(company => ({ value: company.id, label: company.name })), [companies]);
  const fromCityOptions = useMemo(() => cities.map(city => ({ value: city.name, label: city.name, location: city.location })), [cities]);
  const toCityOptions = useMemo(() => cities.map(city => ({ value: city.name, label: city.name, location: city.location })), [cities]);
  const carTypeOptions = useMemo(() => carTypes.map(carType => ({ value: carType.id, label: carType.name })), [carTypes]);
  const tripTypeOptions = useMemo(() => tripTypes.map(tripType => ({ value: tripType.id, label: tripType.name })), [tripTypes]);
  const garageOptions = useMemo(() => garageData.map(garage => ({ value: garage.id, label: garage.address })), [garageData]);
  const labelOptions = useMemo(() => labels.map(label => ({ value: label.id, label: label.name })), [labels]);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState(null); // 'reporting' or 'drop'
  const [pendingCoords, setPendingCoords] = useState(null);

  const [reportingAddressSuggestions, setReportingAddressSuggestions] = useState([]);
  const [dropAddressSuggestions, setDropAddressSuggestions] = useState([]);

  const [mapReportingAddress, setMapReportingAddress] = useState({});
  const [mapDropAddress, setMapDropAddress] = useState({});

  const fetchFile = async (imagePath) => {
    if (!imagePath) return;

    try {
      // Get a presigned URL for direct download
      const response = await ApiManager.storageGetPresignedDownloadUrl(imagePath);

      if (!response.ok) {
        throw new Error("Failed to generate presigned URL");
      }

      const result = await response.json();

      // The response contains: url, fileName, expires
      console.log("Presigned download URL data:", result);

      // Open the presigned URL directly in a new tab
      window.open(result.url, "_blank");
      console.log("Download link will expire in:", result.expires);
    } catch (error) {
      console.error("Error fetching file:", error);
      toast.error("Failed to download file. Please try again.");
    }
  };

  const handleFileUpload = async (file, index) => {
    if (!file) return;

    try {
      // Delete existing file if there is one
      const existingFile = selectedBillingItems[index]?.imagePath;
      if (existingFile) {
        await deleteStoredFile(existingFile);
      }

      // Get a presigned URL for upload
      const presignedResponse = await ApiManager.storageGetPresignedUploadUrl({
        filename: file.name,
        directory: "images/receipts"
      });

      if (!presignedResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const presignedData = await presignedResponse.json();

      // The response contains: url, fileName, key, expires
      console.log("Presigned URL data:", presignedData);

      // Upload the file directly to the storage provider
      const uploadResponse = await fetch(presignedData.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      // Save metadata after upload using the fileName from the response
      const metadataResponse = await ApiManager.storageSaveMetadata(presignedData.fileName, presignedData.key);

      if (!metadataResponse.ok) {
        throw new Error("Failed to save metadata");
      }

      // Use the fileName from the response for the imagePath
      handleBillingItemChange(index, "imagePath", presignedData.fileName);
      console.log("Upload successful. File will expire in:", presignedData.expires);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const deleteStoredFile = async (fileName) => {
    try {
      const response = await ApiManager.storageDeleteFile(fileName);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      console.log(`File ${fileName} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting file ${fileName}:`, error);
    }
  };

  const handleBillingItemChange = (index, field, value) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index][field] = value;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = [...selectedBillingItems];
    updatedBillingItems[index][field] = value;
    setSelectedBillingItems(updatedBillingItems);
  };

  const removeBillingItem = (index) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index]["cost"] = 0;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = selectedBillingItems.filter(
      (_, i) => i !== index
    );
    setSelectedBillingItems(updatedBillingItems);
  };

  const addBillingItem = () => {
    setSelectedBillingItems([
      ...selectedBillingItems,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
    setBillingItemPayload([
      ...billingItemPayload,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
  };
  const handleLabelSelect = (e) => {
    const selectedId = parseInt(e.target.value);
    if (!selectedLabelIds.includes(selectedId)) {
      setSelectedLabelIds([...selectedLabelIds, selectedId]);
    }
  };

  const handleRemoveLabel = (id) => {
    setSelectedLabelIds(selectedLabelIds.filter((labelId) => labelId !== id));
  };

  const handleTripTypeChange = (e) => {
    e.preventDefault();
    const selectedTripTypeId = parseInt(e.target.value);
    const selectedType = tripTypes.find(
      (type) => type.id === selectedTripTypeId
    );
    setTripType(selectedType);
  };

  const handleReportingAddressSelect = (location) => {
    setMapReportingAddress(location);
  };

  const handleDropAddressSelect = (location) => {
    setMapDropAddress(location);
  };

  // Handler for marker drag end
  const handleMarkerDragEnd = async (id, latlng) => {
    if (id === "reporting") {
      setMapReportingAddress(prev => ({ ...prev, latitude: latlng.latitude, longitude: latlng.longitude }));
    } else if (id === "drop") {
      setMapDropAddress(prev => ({ ...prev, latitude: latlng.latitude, longitude: latlng.longitude }));
    }
    // Fetch address suggestions
    try {
      const params = new URLSearchParams({ lat: latlng.latitude, lng: latlng.longitude });
      const response = await ApiManager.getReverseGeocode(params);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && Array.isArray(data.data.results) && data.data.results.length > 0) {
          if (id === "reporting") {
            setReportingAddressSuggestions(data.data.results);
          } else if (id === "drop") {
            setDropAddressSuggestions(data.data.results);
          }
        }
      }
    } catch (err) {
      setReportingAddressSuggestions([]);
      setDropAddressSuggestions([]);
    }
  };

  // Handler for selecting an address from suggestions (for MapInput)
  const handleSelectAddressSuggestion = (type, suggestion) => {
    if (type === "reporting") {
      setMapReportingAddress(prev => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng
      }));
      setReportingAddressSuggestions([]);
    } else if (type === "drop") {
      setMapDropAddress(prev => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng
      }));
      setDropAddressSuggestions([]);
    }
  };

  useEffect(() => {
    const fetchBillingItems = async () => {
      try {
        await withLoading('fetchBillingItems', async (onProgress) => {
          onProgress(10);
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          onProgress(30);
          const response = await ApiManager.getAllBillingItems(params);
          onProgress(60);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          onProgress(90);
          setBillingItems(json || []);
          onProgress(100);
        }, 'Loading billing items...', 30000);
      } catch (error) {
        console.error("Error fetching data:", error);
        setBillingItems([]);
      }
    };
    fetchBillingItems();
  }, []);
  // Fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        await withLoading('fetchCompanies', async (onProgress) => {
          onProgress(10);
          const companiesParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const companiesResponse = await ApiManager.getAllCompanies(companiesParams);
          if (!companiesResponse.ok) throw new Error("Failed to fetch companies");
          const companiesJson = await companiesResponse.json();
          setCompanies(companiesJson || []);
          onProgress(100);
        }, 'Loading companies...', 30000);
      } catch (error) {
        console.error("Error loading companies:", error);
        toast.error("Failed to load companies. Please refresh the page.");
      }
    };
    fetchCompanies();
  }, []);

  // Fetch car types
  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        await withLoading('fetchCarTypes', async (onProgress) => {
          onProgress(10);
          const carTypesParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const carTypesResponse = await ApiManager.getCarType(carTypesParams);
          if (!carTypesResponse.ok) throw new Error("Failed to fetch car types");
          const carTypesJson = await carTypesResponse.json();
          setCarTypes(carTypesJson || []);
          onProgress(100);
        }, 'Loading car types...', 30000);
      } catch (error) {
        console.error("Error loading car types:", error);
        toast.error("Failed to load car types. Please refresh the page.");
      }
    };
    fetchCarTypes();
  }, []);

  // Fetch garages
  useEffect(() => {
    const fetchGarages = async () => {
      try {
        await withLoading('fetchGarages', async (onProgress) => {
          onProgress(10);
          const garagesParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const garagesResponse = await ApiManager.getAllGarages(garagesParams);
          if (!garagesResponse.ok) throw new Error("Failed to fetch garages");
          const garagesJson = await garagesResponse.json();
          setGarageData(garagesJson || []);
          onProgress(100);
        }, 'Loading garages...', 30000);
      } catch (error) {
        console.error("Error loading garages:", error);
        toast.error("Failed to load garages. Please refresh the page.");
      }
    };
    fetchGarages();
  }, []);

  // Fetch trip types
  useEffect(() => {
    const fetchTripTypes = async () => {
      try {
        await withLoading('fetchTripTypes', async (onProgress) => {
          onProgress(10);
          const tripTypesParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const tripTypesResponse = await ApiManager.getAllTripTypes(tripTypesParams);
          if (!tripTypesResponse.ok) throw new Error("Failed to fetch trip types");
          const tripTypesJson = await tripTypesResponse.json();
          setTripTypes(tripTypesJson || []);
          onProgress(100);
        }, 'Loading trip types...', 30000);
      } catch (error) {
        console.error("Error loading trip types:", error);
        toast.error("Failed to load trip types. Please refresh the page.");
      }
    };
    fetchTripTypes();
  }, []);

  // Fetch cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        await withLoading('fetchCities', async (onProgress) => {
          onProgress(10);
          const citiesParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const citiesResponse = await ApiManager.getAllCities(citiesParams);
          if (!citiesResponse.ok) throw new Error("Failed to fetch cities");
          const citiesJson = await citiesResponse.json();
          setCities(citiesJson || []);
          onProgress(100);
        }, 'Loading cities...', 30000);
      } catch (error) {
        console.error("Error loading cities:", error);
        toast.error("Failed to load cities. Please refresh the page.");
      }
    };
    fetchCities();
  }, []);

  // Fetch labels
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        await withLoading('fetchLabels', async (onProgress) => {
          onProgress(10);
          const labelsParams = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const labelsResponse = await ApiManager.getAllLabels(labelsParams);
          if (!labelsResponse.ok) throw new Error("Failed to fetch labels");
          const labelsJson = await labelsResponse.json();
          setLabels(labelsJson || []);
          onProgress(100);
        }, 'Loading labels...', 30000);
      } catch (error) {
        console.error("Error loading labels:", error);
        toast.error("Failed to load labels. Please refresh the page.");
      }
    };
    fetchLabels();
  }, []);

  // Update loading state based on all data loading states
  useEffect(() => {
    const isAnyLoading = isLoading('fetchCompanies') ||
                        isLoading('fetchCarTypes') ||
                        isLoading('fetchGarages') ||
                        isLoading('fetchTripTypes') ||
                        isLoading('fetchCities') ||
                        isLoading('fetchLabels');
    setShowLoading(isAnyLoading);
  }, [
    isLoading('fetchCompanies'),
    isLoading('fetchCarTypes'),
    isLoading('fetchGarages'),
    isLoading('fetchTripTypes'),
    isLoading('fetchCities'),
    isLoading('fetchLabels')
  ]);

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", phone: "", email: "" }]);
  };

  const addAdditionalContact = () => {
    setAdditionalContacts([
      ...additionalContacts,
      { name: "", phone: "", email: "" },
    ]);
  };

  const removeAdditionalContact = (index) => {
    const updatedContacts = additionalContacts.filter((_, i) => i !== index);
    setAdditionalContacts(updatedContacts);
  };

  const handleAdditionalContactChange = (index, field, value) => {
    const updatedContacts = [...additionalContacts];
    updatedContacts[index][field] = value;
    setAdditionalContacts(updatedContacts);
  };

  const removePassenger = (index) => {
    const updatedPassengers = passengers.filter((_, i) => i !== index);
    setPassengers(updatedPassengers);
  };

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
  };

  const handleGetPrice = async (event) => {
    setShowPrices(true)
    event.preventDefault();
    try {
      await withLoading('getPrice', async () => {
        const form = event.target.closest("form");
        if (!form) {
          console.error("Form not found");
          return;
        }
        const formData = new FormData(form);
        const queryParams = new URLSearchParams({
          trip_type_id: parseInt(formData.get("tripType")),
          car_type_id: parseInt(formData.get("carType")),
          company_id: parseInt(formData.get("company")),
          city: formData.get("fromCity"),
        });

        const response = await ApiManager.getCharges(queryParams);
        if (!response.ok) throw new Error("Failed to fetch price");
        const json = await response.json();

        setCharges(json);
        form.querySelector('[name="price"]').value = json.rates.base_rates || "";
        form.querySelector('[name="perExKmRate"]').value = json.rates?.extra_km_rates || "";
        form.querySelector('[name="perExTimeUnitRate"]').value =
          tripType && tripType.type === "day-km"
            ? json.rates?.extra_day_rates || ""
            : json.rates?.extra_hr_rates || "";
      }, 'Calculating price...', 30000);
    } catch (error) {
      console.error("Error fetching price:", error);
      toast.error("Failed to calculate price. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await withLoading('submitForm', async (updateProgress) => {
      updateProgress(10, 'Processing form data...');

    const formData = new FormData(e.target);
    const tripData = Object.fromEntries(formData.entries());

    const reportingAddress = mapReportingAddress.description;
    const dropAddress = mapDropAddress.description;

    // Format date and time to UNIX timestamp
    const scheduledStartTime =
      new Date(tripData.scheduledStartTime).getTime() / 1000;

      updateProgress(30, 'Preparing trip data...');

    const formattedTripData = {
      companyId: parseInt(tripData.company), // Assuming it's an ID
      fromCity: tripData.fromCity,
      toCity: tripData.toCity,
      // shortReportingAddress: tripData.shortReportingAddress,
      carType: parseInt(tripData.carType), // Assuming it's an ID
      tripType: parseInt(tripData.tripType), // Assuming it's an ID
      garageId: parseInt(selectedGarage),
      scheduledStartTime: scheduledStartTime,
      scheduledEndTime: tripData.scheduledEndTime
        ? new Date(tripData.scheduledEndTime).getTime() / 1000
        : null,
      reportingTime: tripData.reportingTime
        ? new Date(tripData.reportingTime).getTime() / 1000
        : null,
      reportingAddress: reportingAddress,
      dropAddress: dropAddress,
      passengers: passengers.filter((p) => p.name || p.phone || p.email), // Filter out empty passengers
      bookedBy: {
        name: tripData.bookedByName,
        phone: tripData.bookedByPhone,
        email: tripData.bookedByEmail,
      },
      // additionalContacts: additionalContacts.filter(c => c.name || c.phone || c.email), // Filter out empty contacts
      labels: selectedLabelIds,
      remarks: tripData.remarks || "",
      operatorNotes: tripData.operatorNotes || "",
      contractAutoSwitchEnabled: contractAutoSwitchEnabled,
      ...(showPrices && {
        charges: {
          ...charges,
          rates: {
            ...charges.rates,
            base_rates: parseFloat(tripData.price) || 0,
            extra_km_rates: parseFloat(tripData.perExKmRate) || 0,
            ...(tripType?.type === "day-km" && {
              extra_day_rates: parseFloat(tripData.perExTimeUnitRate) || 0,
            }),
            ...(tripType?.type !== "day-km" && {
              extra_hr_rates: parseFloat(tripData.perExTimeUnitRate) || 0,
            }),
          },
        },
      }),
      reportingCoordinates: {
        latitude: mapReportingAddress.latitude,
        longitude: mapReportingAddress.longitude,
      },
      dropCoordinates: {
        latitude: mapDropAddress.latitude,
        longitude: mapDropAddress.longitude,
      },
      TripBillingItems: billingItemPayload
        .filter((item) => item.billingItemId) // Ensure billingItemId exists
        .map((item) => ({
          ...(item.id !== null && item.id !== undefined
            ? { id: Number(item.id) }
            : {}),
          billingItemId: Number(item.billingItemId),
          cost: parseFloat(item.cost),
          imagePath: item.imagePath || "",
          notes: item.notes || "",
        })),
      // operatorNotes: tripData.operatorNotes || "",
      // price: parseFloat(tripData.price) || 0,
      // perExKmRate: parseFloat(tripData.perExKmRate) || 0,
      // perExTimeUnitRate: parseFloat(tripData.perExTimeUnitRate) || 0,
    };

    console.log("Formatted Trip Data:", formattedTripData);
    try {

      updateProgress(80, 'Creating trip...');

      const response = await ApiManager.addTrip(formattedTripData);
      if (response.ok) {
        // alert("Trip successfully created!");
        const data = await response.json(); // Parse the JSON response
        console.log("Fetched data:", data);
        console.log("trip Id:", data.trip.tripId);
        updateProgress(100, 'Trip updated and booked successfully!');
        navigate(`/login/trips/booking-details/${data.trip.tripId}`, {
          state: {
            showToast: true,
            toastMessage: "Trip created successfully!",
          },
        });
      } else {
        const errorData = await response.json();
          console.error("Trip creation failed:", errorData);
          updateProgress(90, 'Error creating trip. Handling errors...');
          throw new Error(errorData.message || "Failed to create trip");
      }
    } catch (error) {
      console.error("Network Error:", error);
      toast.error(error.message || "Something went wrong. Please try again.");
    }
  }
)};

  const showToast = (message) => {
    toast.success(message, {
      position: "top-center", //toast.POSITION.TOP_CENTER,
      autoClose: 3000, // 3 seconds
    });
  };

  const isDataLoading = isLoading('loadInitialData') ||
                       isLoading('fetchBillingItems') ||
                       isLoading('fetchSuggestions') ||
                       isLoading('getPrice');

  if (isLoading('fetchCompanies') || isLoading('fetchDrivers') ||
      isLoading('fetchCars') || isLoading('fetchCarTypes') ||
      isLoading('fetchTripTypes') || isLoading('fetchLabels') ||
      isLoading('fetchGarages') || isLoading('fetchCities')) {
  return (
      <div>
            <LoadingSpinner
              message={getMessage('fetchCompanies') || getMessage('fetchDrivers') || getMessage('fetchCars') ||
                      getMessage('fetchCarTypes') || getMessage('fetchTripTypes') || getMessage('fetchLabels') ||
                      getMessage('fetchGarages') || getMessage('fetchCities')}
              progress={getProgress('fetchCompanies') || getProgress('fetchDrivers') || getProgress('fetchCars') ||
                       getProgress('fetchCarTypes') || getProgress('fetchTripTypes') || getProgress('fetchLabels') ||
                       getProgress('fetchGarages') || getProgress('fetchCities')}
              showProgress={true}
            />
      </div>
    );
  }

  return (
        <div className="addNewTrip">
          <ToastContainer />
          <form onSubmit={handleSubmit}>
            {/* Company Section */}
            <fieldset className="fieldset">
              <legend>Company Info</legend>
              <div className="formGroup">
                <label>Company</label>
                <CustomSelect
                  name="company"
                  options={companyOptions}
                  value={selectedCompany}
                  onChange={(selectedOption) => setSelectedCompany(selectedOption?.value || "")}
                  placeholder="Select a Company"
                  isDisabled={loadingCompanies}
                  required
                />
              </div>
            </fieldset>

            {/* Booked By / Passenger Section */}
            <fieldset className="fieldset">
              <legend>Booked By</legend>
              <div className="row">
                <div className="formGroup">
                  <label>Name</label>
                  <input
                    type="text"
                    name="bookedByName"
                    placeholder="Name"
                    className="input"
                  />
                </div>
                <div className="formGroup">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="bookedByPhone"
                    placeholder="Phone"
                    className="input"
                    pattern="[0-9]{10}" // For a 10-digit number
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10); // Limit to 10 digits
                      }
                    }}
                  />
                </div>
                <div className="formGroup">
                  <label>Email</label>
                  <input
                    type="email"
                    name="bookedByEmail"
                    placeholder="Email"
                    className="input"
                  />
                </div>
              </div>

              {additionalContacts.map((contact, index) => (
                <div key={index} className="additionalContactsRow">
                  <div className="formGroup">
                    <label>Additional Contact Name</label>
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) =>
                        handleAdditionalContactChange(index, "name", e.target.value)
                      }
                      placeholder={`Contact ${index + 1} Name`}
                      className="input"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          index,
                          "phone",
                          e.target.value
                        )
                      }
                      placeholder="Phone"
                      className="input"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Email</label>
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          index,
                          "email",
                          e.target.value
                        )
                      }
                      placeholder="Email"
                      className="input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAdditionalContact(index)}
                    className="remove-button"
                  >
                    x
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addAdditionalContact}
                className="custom-button"
              >
                + Add Additional Contact
              </button>
            </fieldset>

            <fieldset className="fieldset">
              <legend>Passenger Details</legend>

              {passengers.map((passenger, index) => (
                <div key={index} className="passengerRow">
                  <div className="formGroup">
                    <label>Passenger Name</label>
                    <input
                      type="text"
                      value={passenger.name}
                      onChange={(e) =>
                        handlePassengerChange(index, "name", e.target.value)
                      }
                      placeholder="Passenger Name"
                      className="input"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Passenger Phone Number</label>
                    <input
                      type="tel"
                      value={passenger.phone}
                      onChange={(e) =>
                        handlePassengerChange(index, "phone", e.target.value)
                      }
                      placeholder="Passenger Phone"
                      className="input"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Passenger Email</label>
                    <input
                      type="email"
                      value={passenger.email}
                      onChange={(e) =>
                        handlePassengerChange(index, "email", e.target.value)
                      }
                      placeholder="Passenger Email"
                      className="input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removePassenger(index)}
                    className="remove-button"
                  >
                    x
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addPassenger}
                className="custom-button"
              >
                + Add Another Passenger
              </button>
            </fieldset>

            <fieldset className="fieldset">
              <legend>Address Details</legend>
              <div className="row">
                <div className="formGroup">
                  <label>From City </label>
                  <CustomSelect
                    name="fromCity"
                    options={fromCityOptions}
                    value={selectedFromCity}
                    onChange={(selectedOption) => {
                      setSelectedFromCity(selectedOption?.value || "");
                      setFromCityLocation(selectedOption?.location || null);
                    }}
                    placeholder="Select a city"
                    isLoading={isLoading('fetchCities')}
                  />
                </div>
                <div className="formGroup">
                  <label>To City</label>
                  <CustomSelect
                    name="toCity"
                    options={toCityOptions}
                    value={selectedToCity}
                    onChange={(selectedOption) => {
                      setSelectedToCity(selectedOption?.value || "");
                      setToCityLocation(selectedOption?.location || null);
                    }}
                    placeholder="Select a city"
                    isLoading={isLoading('fetchCities')}
                  />
                </div>
              </div>

              {/* Address Inputs (no maps inside) */}
              <div className="mapContainer">
                {/* Reporting Address Section */}
                <div>
                  <MapInput
                    label="Reporting Address"
                    onAddressSelect={handleReportingAddressSelect}
                    name="reportingAddress"
                    cityLocation={fromCityLocation}
                    initialValue={mapReportingAddress}
                    addressSuggestions={reportingAddressSuggestions}
                    onSelectAddressSuggestion={suggestion => handleSelectAddressSuggestion("reporting", suggestion)}
                  />
                </div>

                {/* Drop Address Section */}
                <div>
                  <MapInput
                    label="Drop Address"
                    onAddressSelect={handleDropAddressSelect}
                    name="dropAddress"
                    cityLocation={toCityLocation}
                    initialValue={mapDropAddress}
                    addressSuggestions={dropAddressSuggestions}
                    onSelectAddressSuggestion={suggestion => handleSelectAddressSuggestion("drop", suggestion)}
                  />
                </div>
              </div>


              {/* Combined MapView for Reporting and Drop Address */}
              {(mapReportingAddress?.latitude || mapDropAddress?.latitude) && (
                <div className="mapContainer">
                  <MapView
                    markers={[
                      mapReportingAddress && mapReportingAddress.latitude && mapReportingAddress.longitude ? {
                        latitude: mapReportingAddress.latitude,
                        longitude: mapReportingAddress.longitude,
                        label: "Reporting Address",
                        draggable: true,
                        id: "reporting"
                      } : null,
                      mapDropAddress && mapDropAddress.latitude && mapDropAddress.longitude ? {
                        latitude: mapDropAddress.latitude,
                        longitude: mapDropAddress.longitude,
                        label: "Drop Address",
                        draggable: true,
                        id: "drop"
                      } : null
                    ].filter(Boolean)}
                    zoom={13}
                    height={450}
                    onMarkerDragEnd={handleMarkerDragEnd}
                    showDirections={true}
                  />
                </div>
              )}
            </fieldset>

            {/* Trip Details Section */}
            <fieldset className="fieldset">
              <legend>Trip Details</legend>
              <div className="row">
                <div className="formGroup">
                  <label>Car Type</label>
                  <CustomSelect
                    name="carType"
                    options={carTypeOptions}
                    value={selectedCarType}
                    onChange={(selectedOption) => setSelectedCarType(selectedOption?.value || "")}
                    placeholder="Select a Car Type"
                    isDisabled={loadingCarTypes}
                  />
                  {loadingCarTypes && <p>Loading Car Types...</p>}
                </div>
                <div className="formGroup">
                  <label>Trip Type </label>
                  <CustomSelect
                    name="tripType"
                    options={tripTypeOptions}
                    value={selectedTripType}
                    onChange={(selectedOption) => setSelectedTripType(selectedOption?.value || "")}
                    placeholder="Select a Trip Type"
                    isDisabled={loadingTripTypes}
                  />
                  {loadingTripTypes && <p>Loading Trip Types...</p>}
                </div>
                <div className="formGroup">
                  <label>Garage </label>
                  <CustomSelect
                    name="garage"
                    options={garageOptions}
                    value={selectedGarage}
                    onChange={(selectedOption) => setSelectedGarage(selectedOption?.value || "")}
                    placeholder="Select Garage"
                  />
                </div>
                <div className="formGroup">
                  <label htmlFor="contractAutoSwitchEnabledToggle">Contract Auto Switch</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label className="toggle-switch">
                      <input
                        id="contractAutoSwitchEnabledToggle"
                        type="checkbox"
                        checked={contractAutoSwitchEnabled}
                        onChange={() => setContractAutoSwitchEnabled((prev) => !prev)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span>{contractAutoSwitchEnabled ? 'On' : 'Off'}</span>
                  </div>
                </div>
              </div>

              {/* Second Row */}
              <div className="row">
                <div className="formGroup">
                  <label>Scheduled Start Time</label>
                  <input
                    type="datetime-local"
                    name="scheduledStartTime"
                    className="input"
                  />
                </div>
                <div className="formGroup">
                  <label>Scheduled End Time</label>
                  <input
                    type="datetime-local"
                    name="scheduledEndTime"
                    className="input"
                  />
                </div>
                <div className="formGroup">
                  <label>Reporting Time</label>
                  <input
                    type="datetime-local"
                    name="reportingTime"
                    placeholder="Time in minutes"
                    className="input"
                  />
                </div>
              </div>
            </fieldset>

            {/* forth Row */}
            <fieldset className="fieldset">
              <legend>Charges</legend>
              <div className="row">
                {showPrices && (
                  <>
                    <div className="formGroup">
                      <label>Price</label>
                      <input
                        type="text"
                        name="price"
                        placeholder="Enter price"
                        className="input"
                      />
                    </div>
                    <div className="formGroup">
                      <label>Per Extra KM rate</label>
                      <input
                        type="text"
                        name="perExKmRate"
                        placeholder="Enter extra KM rate"
                        className="input"
                      />
                    </div>
                    <div className="formGroup">
                      <label>
                        {tripType && tripType.type === "day-km"
                          ? "Per Extra Day rate"
                          : "Per Extra Hr rate"}
                      </label>
                      <input
                        type="text"
                        name="perExTimeUnitRate"
                        placeholder="Enter extra hour rate"
                        className="input"
                      />
                    </div>
                  </>
                )}
                <div className="formGroup getPriceButton">
                  <button
                    type="button"
                    onClick={handleGetPrice}
                    className={`custom-button ${isLoading('getPrice') ? 'loading' : ''}`}
                    disabled={isLoading('getPrice')}
                  >
                    {isLoading('getPrice') ? (
                      <LoadingSpinner
                        size="xs"
                        inline={true}
                        message="Getting price..."
                      />
                    ) : (
                      "Get Price"
                    )}
                  </button>
                </div>
              </div>
            </fieldset>

            <div className="row">
              <div className="formGroup">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  placeholder="Enter remarks"
                  className="input remarks"
                  rows="3"
                />
              </div>
              <div className="formGroup">
                <label>Operator Notes</label>
                <textarea
                  name="operatorNotes"
                  placeholder="Enter operator notes"
                  className="input operatorNotes"
                  rows="3"
                />
              </div>
            </div>
            <fieldset className="fieldset">
              <legend>Billing Items</legend>
              {(selectedBillingItems || []).map((selectedBillingItem, index) => (
                <div key={index} className="passengerRow">
                  <div className="formGroup">
                    <label>Billing Item</label>
                    <CustomSelect
                      className="input"
                      options={billingItems.map(billingItem => ({ value: billingItem.billingItemId, label: billingItem.name }))}
                      value={selectedBillingItem.billingItemId}
                      onChange={(selectedOption) => handleBillingItemChange(index, "billingItemId", selectedOption?.value || "")}
                      placeholder="Select a billing Item"
                    />
                  </div>
                  <div className="formGroup">
                    <label>Cost</label>
                    <input
                      type="number"
                      value={selectedBillingItem.cost}
                      onChange={(e) =>
                        handleBillingItemChange(index, "cost", e.target.value)
                      }
                      className="input"
                    />
                  </div>
                  <div className="formGroup">
                    <div className="file-upload-section">
                      <div className="file-label-container">
                        <label>Receipt</label>
                        {selectedBillingItem.imagePath && (
                          <div className="file-preview">
                            <button
                              type="button"
                              className="view-receipt-btn"
                              onClick={() =>
                                fetchFile(selectedBillingItem.imagePath)
                              }
                            >
                              Receipt
                            </button>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        className="input"
                        onChange={(e) => handleFileUpload(e.target.files[0], index)}
                      />
                    </div>
                  </div>

                  <div className="formGroup">
                    <label>Notes</label>
                    <input
                      type="textarea"
                      value={selectedBillingItem.notes}
                      onChange={(e) =>
                        handleBillingItemChange(index, "notes", e.target.value)
                      }
                      className="input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeBillingItem(index)}
                    className="remove-button"
                  >
                    x
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addBillingItem}
                className="custom-button"
              >
                + Add Billing Item
              </button>
            </fieldset>


            <fieldset className="fieldset">
          <legend>Labels</legend>
          <div className="formGroup">
            <label>Select Labels</label>
            <CustomSelect
              options={(labels || []).map(label => ({
                value: label.id,
                label: label.name
              }))}
              value=""
              onChange={(selectedOption) => {
                if (selectedOption?.value && !selectedLabelIds.includes(selectedOption.value)) {
                  setSelectedLabelIds([...selectedLabelIds, selectedOption.value]);
                }
              }}
              placeholder="Select a Label"
            />

            {/* Display selected labels */}
            {selectedLabelIds.length > 0 && (
              <div className="selectedLabels">
                {(selectedLabelIds || []).map((id) => {
                  const label = (labels || []).find((label) => label.id === id);
                  if (!label) return null;

                  const backgroundColor = label.color;
                  const textColor = getContrastColor(backgroundColor);

                  return (
                    <div
                      key={id}
                      className="selectedLabel"
                      style={{ backgroundColor: label.color, color: textColor }}
                    >
                      {label.name}
                      <button
                        style={{ color: textColor }}
                        onClick={() => handleRemoveLabel(id)}
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
         </fieldset>


            {/* Submit Button */}

            <button
                    type="submit"
                    className={`submitButton ${isLoading('submitForm') ? 'loading' : ''}`}
                  >
                    {isLoading('submitForm') ? (
                      <LoadingSpinner
                        size="xs"
                        inline={true}
                        message="Creating Trip..."
                      />
                    ) : (
                      "Submit"
                    )}
                  </button>
          </form>
          {/* Address Suggestions Modal */}
          {showAddressModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Select Address</h3>
                <ul>
                  {addressSuggestions.map((addr, idx) => (
                    <li key={idx}>
                      <button onClick={() => handleSelectAddressSuggestion(addr)}>
                        {addr.formatted_address}
                      </button>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setShowAddressModal(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
  );
};

export default AddNewTrip;
